import type { PlainWorkflowExecution } from "@/queries/automation-queries";
import type { FC } from "react";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import { triggerName } from "@/utils/automations";

type ExecutionTriggerInfoProps = {
    execution: PlainWorkflowExecution;
    members?: PlainMessage<OrganizationMember>[];
};

export const ExecutionTriggerInfo: FC<ExecutionTriggerInfoProps> = ({ execution, members }) => {
    const triggerType = triggerName(execution.spec?.trigger);

    const creator =
        execution.metadata?.creator?.id && members
            ? members.find((m) => m.userId === execution.metadata?.creator?.id)
            : undefined;

    const executor =
        execution.metadata?.executor?.id && members
            ? members.find((m) => m.userId === execution.metadata?.executor?.id)
            : undefined;

    const isSameUser = creator?.userId === executor?.userId;

    if (isSameUser && creator) {
        return (
            <div className="flex flex-wrap items-center gap-1.5 text-sm text-content-muted">
                <span className="text-content-primary">{triggerType}</span>
                <span className="text-content-inactive">•</span>
                <span>Triggered / Ran as:</span>
                <Avatar size={16}>
                    <AvatarImage src={creator.avatarUrl} alt={creator.fullName} />
                    <AvatarFallback>
                        <AvatarInitials name={creator.fullName} size={16} />
                    </AvatarFallback>
                </Avatar>
                <span className="text-content-primary">{creator.fullName}</span>
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1.5 text-sm text-content-muted">
            <span className="text-content-primary">{triggerType}</span>
            {creator && (
                <>
                    <span className="text-content-inactive">•</span>
                    <span>Triggered by:</span>
                    <Avatar size={16}>
                        <AvatarImage src={creator.avatarUrl} alt={creator.fullName} />
                        <AvatarFallback>
                            <AvatarInitials name={creator.fullName} size={16} />
                        </AvatarFallback>
                    </Avatar>
                    <span className="text-content-primary">{creator.fullName}</span>
                </>
            )}
            {executor && (
                <>
                    <span className="text-content-inactive">•</span>
                    <span>Ran as:</span>
                    <Avatar size={16}>
                        <AvatarImage src={executor.avatarUrl} alt={executor.fullName} />
                        <AvatarFallback>
                            <AvatarInitials name={executor.fullName} size={16} />
                        </AvatarFallback>
                    </Avatar>
                    <span className="text-content-primary">{executor.fullName}</span>
                </>
            )}
        </div>
    );
};
